import { isEmpty, merge } from 'lodash-es';
import { Children, type ReactNode } from 'react';
import { isElement } from 'react-is';

import { Intent, type IntentProps } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import type { AnnotationsTooltipConfig } from '../../core/components/annotations/components/tooltip/types/annotations-tooltip-props.js';
import type { AnnotationsMarkerConfig } from '../../core/components/annotations/types/annotations-marker-props.js';
import type { AnnotationsTrackConfig } from '../../core/components/annotations/types/annotations-track-props.js';
import { populateMissingMarkerProps } from '../../core/components/annotations/utils/populate-missing-marker-props.js';
import { populateMissingTrackProps } from '../../core/components/annotations/utils/populate-missing-track-props.js';
import { ChartInteractions } from '../../core/slots/chart-interactions/ChartInteractions.js';
import { ColorRule, isValidColorRule } from '../../core/slots/ColorRule.js';
import { ChartToolbar } from '../../core/slots/toolbar/ChartToolbar.js';
import { sanitizeProps } from '../../core/slots/toolbar/CompactToolbar.js';
import { DownloadData } from '../../core/slots/toolbar/DownloadData.js';
import { ChartInteractionsConfig } from '../../core/types/interactions.js';
import { extractBehaviorTrackingProps } from '../../core/utils/extract-behavior-tracking-props.js';
import { getConfigPlusDefaultOrEmpty } from '../../core/utils/interactions.js';
import { iterateChartInteractionsSlots } from '../../core/utils/iterate-chart-interactions-slots.js';
import { Annotations } from '../slots/annotations/Annotations.js';
import { Marker } from '../slots/annotations/Marker.js';
import { AnnotationsTooltip } from '../slots/annotations/Tooltip.js';
import { Track } from '../slots/annotations/Track.js';
import { TimeseriesChartDownloadCSVSlot } from '../slots/DownloadCSV.js';
import { Legend } from '../slots/Legend.js';
import { Pan } from '../slots/Pan.js';
import { Select } from '../slots/Select.js';
import { Area, TimeseriesChartAreaProps } from '../slots/shapes/Area.js';
import { Band } from '../slots/shapes/Band.js';
import { Bar, TimeseriesChartBarProps } from '../slots/shapes/Bar.js';
import { Line, TimeseriesChartLineProps } from '../slots/shapes/Line.js';
import { ThresholdIndicator } from '../slots/ThresholdIndicator.js';
import { Toolbar } from '../slots/Toolbar.js';
import { Tooltip } from '../slots/Tooltip.js';
import { XAxis } from '../slots/XAxis.js';
import { YAxis } from '../slots/YAxis.js';
import { Zoom } from '../slots/Zoom.js';
import { TimeseriesChartBandProps } from '../types/timeseries-band.js';
import { TimeseriesChartSlots } from '../types/timeseries-config.js';
import {
  ChartVariantInternal,
  TimeseriesAreaSlotProps,
  TimeseriesBandSlotProps,
  TimeseriesBarSlotProps,
  TimeseriesLineSlotProps,
  TimeseriesPerVariant,
} from '../types/timeseries.js';
import { TimeseriesChartToolbarConfig } from '../types/toolbar.js';

/**
 * Generate chart config based on slots
 * @param children - TimeseriesChart children
 * @returns TimeseriesChartSlots slot config
 */

const slotInteractionsDefaultValues = {
  zoom: { enabled: true },
  zoomX: { enabled: true },
  pan: { enabled: true },
};

const chartInteractionsDefaultValues = {
  zoom: { enabled: false },
  zoomX: { enabled: false },
  pan: { enabled: false },
};

const getInteractionsConfig = (
  chartConfig: ChartInteractionsConfig,
  slotsConfig: ChartInteractionsConfig,
) => {
  const chartApiValues = getConfigPlusDefaultOrEmpty(
    chartInteractionsDefaultValues,
    chartConfig,
  );
  const slotsApiValues = getConfigPlusDefaultOrEmpty(
    slotInteractionsDefaultValues,
    slotsConfig,
  );
  return merge({}, chartApiValues, slotsApiValues);
};

const disabledOnlyWhenSpecified = ({ disabled }: { disabled?: boolean }) =>
  disabled;

export const iterateConfigSlots = (
  children: ReactNode,
): TimeseriesChartSlots => {
  let slots: TimeseriesChartSlots = {
    toolbar: {},
    legend: {},
    xAxis: {},
    tooltip: {},
    yAxis: [],
    thresholds: [],
    interactions: {},
    colorRules: [],
  };

  let intents: IntentProps[] | undefined;

  let slotToolbar: TimeseriesChartToolbarConfig = {};
  let chartToolbar: TimeseriesChartToolbarConfig = {};
  let slotInteractions = {};
  let chartInteractions = {};

  Children.forEach(children, (child) => {
    /* eslint-disable-next-line @typescript-eslint/no-deprecated */
    if (isSlot(child, ChartInteractions)) {
      const { children, ...previousInteractions } = child.props;
      const interactions = iterateChartInteractionsSlots(children);

      chartInteractions = {
        ...previousInteractions,
        ...interactions,
      };
      // eslint-disable-next-line @typescript-eslint/no-deprecated
    } else if (isSlot(child, ChartToolbar)) {
      // TODO APPDEV-15615: Remove deprecated ChartToolbar handling
      const { children, ...toolbar } = child.props;
      const childrenToolbar = iterateChartToolbarSlots(children);

      chartToolbar = {
        enabled: true,
        ...toolbar,
        ...childrenToolbar,
      };
    } else if (isSlot(child, Toolbar)) {
      // TODO: APPDEV-15615: Remove enabled key after deprecation
      slotToolbar = {
        enabled: child.props.hidden !== true,
        ...slotToolbar,
        ...sanitizeProps(child.props),
        ...extractBehaviorTrackingProps(child.props),
      };
    } else if (isSlot(child, TimeseriesChartDownloadCSVSlot)) {
      slotToolbar = { ...slotToolbar, downloadData: { enabled: true } };
    } else if (isSlot(child, Zoom)) {
      const enabled = !disabledOnlyWhenSpecified(child.props);

      slotInteractions = {
        ...slotInteractions,
        zoom: { enabled },
        zoomX: { enabled },
      };
    } else if (isSlot(child, Pan)) {
      const enabled = !disabledOnlyWhenSpecified(child.props);
      slotInteractions = { ...slotInteractions, pan: { enabled } };
    } else if (isSlot(child, Legend)) {
      const legend = child.props;

      slots = {
        ...slots,
        legend,
      };
    } else if (isSlot(child, Intent)) {
      intents = intents ? [...intents, child.props] : [child.props];
    } else if (isSlot(child, XAxis)) {
      const xAxis = child.props;
      slots = {
        ...slots,
        xAxis,
      };
    } else if (isSlot(child, YAxis)) {
      const yAxis = child.props;
      slots.yAxis.push(yAxis);
    } else if (isSlot(child, Tooltip)) {
      const tooltip = child.props;
      slots = {
        ...slots,
        tooltip,
      };
    } else if (isSlot(child, Annotations)) {
      const { children, ...annotationsProps } = child.props;

      const { tracks, tooltip } = iterateTrackSlots(children);

      const annotations = {
        ...annotationsProps,
        tracks,
        tooltip,
      };

      slots = {
        ...slots,
        annotations,
      };
    } else if (isSlot(child, ThresholdIndicator)) {
      const thresholdProps = child.props;
      slots.thresholds.push(thresholdProps);
    } else if (isSlot(child, Select)) {
      const select = child.props;

      slots = {
        ...slots,
        select,
      };
    } else if (isSlot(child, ColorRule)) {
      const colorRule = child.props;
      if (isValidColorRule(colorRule)) {
        slots.colorRules.push(colorRule);
      }
    }
  });

  const hasInteractionConfig =
    Object.keys(chartInteractions).length > 0 ||
    Object.keys(slotInteractions).length > 0;

  slots.interactions = hasInteractionConfig
    ? getInteractionsConfig(chartInteractions, slotInteractions)
    : {};

  const hasToolbarConfig =
    !isEmpty(slotToolbar) || !isEmpty(chartToolbar) || intents;

  if (hasToolbarConfig) {
    slots.toolbar = {
      ...chartToolbar,
      ...slotToolbar,
      enabled: slotToolbar.enabled ?? chartToolbar.enabled ?? true,
      intents,
    };
  }

  return slots;
};

/**
 * Generate config based on the Tracks and Markers slots
 * @param children - Annotations children
 * @returns tracks configuration including the markers
 */
const iterateTrackSlots = (children: ReactNode) => {
  const tracks: AnnotationsTrackConfig[] = [];
  let tooltip: AnnotationsTooltipConfig = {
    template: undefined,
    hidden: false,
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, Track)) {
      const { children, ...trackProps } = child.props;
      const currentTrack = {
        ...trackProps,
        markers: [] as AnnotationsMarkerConfig[],
      };

      Children.forEach(children, (child) => {
        if (isSlot(child, Marker)) {
          const marker = populateMissingMarkerProps(child.props, trackProps);

          currentTrack.markers.push(marker);
        }
      });

      const track = populateMissingTrackProps(currentTrack);
      tracks.push(track);
    } else if (isSlot(child, AnnotationsTooltip)) {
      const { children, hidden } = child.props;
      tooltip = {
        template: children,
        hidden: hidden ?? false,
      };
    }
  });

  return { tracks, tooltip };
};

/**
 * Generate chart config based on slots
 * @param children - CharInteraction children
 * @returns ChartInteraction slot config
 */
const iterateChartToolbarSlots = (
  children: ReactNode,
): TimeseriesChartToolbarConfig => {
  let toolbar = {};

  Children.forEach(children, (childToolbar) => {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    if (isElement(childToolbar) && childToolbar.type === DownloadData) {
      toolbar = {
        ...toolbar,
        downloadData: { enabled: true },
      };
    }
  });

  return toolbar;
};

/**
 * Generate chart config based on slots
 * @param children - chartSeries children
 * @param defaultVariant - The variant defined at root level of TimeseriesChart
 * @returns ChartSeries slot config
 */
export const iterateChartSeriesSlots = (
  children: ReactNode,
): TimeseriesPerVariant => {
  const line: TimeseriesLineSlotProps[] = [];
  const bar: TimeseriesBarSlotProps[] = [];
  const area: TimeseriesAreaSlotProps[] = [];
  const band: TimeseriesBandSlotProps[] = [];
  const shapePriority: ChartVariantInternal[] = [];

  Children.forEach(children, (childSlot) => {
    if (isElement(childSlot)) {
      switch (childSlot.type) {
        case Line: {
          const { data, ...overrides } =
            childSlot.props as TimeseriesChartLineProps;
          line.push({ ...data, ...overrides });
          if (!shapePriority.includes('line')) {
            shapePriority.push('line');
          }
          break;
        }
        case Bar: {
          const { data, ...overrides } =
            childSlot.props as TimeseriesChartBarProps;
          bar.push({ ...data, ...overrides });
          if (!shapePriority.includes('bar')) {
            shapePriority.push('bar');
          }
          break;
        }
        case Area: {
          const { data, ...overrides } =
            childSlot.props as TimeseriesChartAreaProps;
          area.push({ ...data, ...overrides });
          if (!shapePriority.includes('area')) {
            shapePriority.push('area');
          }
          break;
        }
        case Band: {
          const {
            data,
            strokeOnly = false,
            color,
            gapPolicy,
          } = childSlot.props as TimeseriesChartBandProps;
          band.push({ ...data, strokeOnly, color, gapPolicy });
          if (!shapePriority.includes('band')) {
            shapePriority.push('band');
          }
          break;
        }
      }
    }
  });

  return { band, line, area, bar, shapePriority };
};
